/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useRef } from "react";
import { styled } from '@mui/material/styles';
import { Box, Table, TableBody, TableCell, Card, TableContainer, tableCellClasses, TableHead, TableRow, Link } from '@mui/material';
import Paper from '@mui/material/Paper';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import "./SearchResultsComponent.scss";

const SearchResultsComponent = ({ searchResults, handleNavigate })  => {
  const [rows, setRows] = useState([]);
  const resultsRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const rowsPerPage = 10;
  const keyMap = {
    'Policy/ Quote#': 'quotePolicyNum',
    'Last modified': 'lastModified',
    'Agent#': 'agentNum'
  };
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      fontSize: 16,
      textAlign: 'left',
      borderLeft: '1px solid grey',
      borderBottom: '4px solid black',
      padding: '1.25rem .313rem',
      cursor: 'pointer',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 16,
      textAlign: 'left',
      borderLeft: '1px solid grey !important',
      width: '140px',
      padding: '1.25rem .313rem',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: '#f5f8fa',
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    borderLeft: '1px solid grey',
    width: '140px',
  }));

  function createData(quotePolicyNum, lastModified, agentNum, status, name, city, state, zip, product, source) {
    return { quotePolicyNum, lastModified, agentNum, status, name, city, state, zip, product, source };
  }

  const convertDataToRows = async () => {
    const data = searchResults.map(item => 
      createData(
        item.policyNumber ?? item.quoteNumber,
        item.transactionDate,
        item.agentNumber,
        item.policyStatusCode,
        item.lastName + ', ' + item.firstName,
        item.address.city,
        item.address.state,
        item.address.postalCode,
        item.lob.lineOfBusiness,
        item.sourceSystem
      )
    );
    return data;
  }
  // Pagination handlers
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  useEffect(() => {
    async function fetchData() {
      if(searchResults[0] === '') { 
        return ['']; 
      }
      const rowsData = await convertDataToRows();
      setRows(rowsData);
    }

    setCurrentPage(1);
    fetchData();
  }, [searchResults]);
  
  useEffect(() => {
    if (rows.length > 0) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [rows]);

  const handleSortClick = (columnKey) => {
    let direction = 'asc';

    if (sortColumn === columnKey && sortDirection === 'asc') {
      direction = 'desc';
    }

    setSortColumn(columnKey);
    setSortDirection(direction);

    handleSort(columnKey, direction);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedRows = React.useMemo(() => {
    let sortableItems = [...rows];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [rows, sortConfig]);

  const currentRows = sortedRows.slice(currentPage * rowsPerPage - rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const handlePrevClick = () => { setCurrentPage(prev => (prev > 1 ? prev - 1 : prev)); };
  const handleNextClick = () => { setCurrentPage(prev => (prev < totalPages ? prev + 1 : prev)); };
  const handlePageClick = (page) => { setCurrentPage(page); };

  return (
    <Box ref={resultsRef} className='searchresults agent-ciam-container'>
      { searchResults[0] === '' ? (
        <Box className='searchresults-table-info-top'>
          <Box className='searchresults-title'>Search results</Box>
          <Box className='searchresults-none'>No search results found.</Box>
        </Box>
      ) : (
      <>
        <Box className='searchresults-table-info-top'>
          <Box className='searchresults-title'>Search results</Box>
          <Box className='searchresults-sub-message'>Your search returned {searchResults.length} result(s). Showing up to 50 results.</Box>
          <Box className='searchresults-results-box'>
          <Box className='searchresults-sub-message'>Viewing {indexOfFirstRow + 1}–{indexOfLastRow > rows.length ? rows.length : indexOfLastRow} of {rows.length} results</Box>
          {searchResults.length > 10 ? (
            <Box className='searchresults-pagination'>
              <Link className='searchresults-prev' component="button" onClick={handlePrevClick} disabled={currentPage === 1}>{`Prev`}</Link>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page =>
                  <Link
                    className={`searchresults-page ${currentPage === page ? 'selected-page' : ''}`}
                    key={page}
                    component="button"
                    onClick={() => handlePageClick(page)}
                    disabled={currentPage === page}
                  >
                    {page}
                  </Link>
                )}
                {/* {totalPages > 3 && (
                  <>
                    <span>...</span>
                    <Link
                      className={`searchresults-page ${currentPage === totalPages ? 'selected-page' : ''}`}
                      component="button" onClick={() => handlePageClick(totalPages)}>{totalPages}
                    </Link>
                  </>
                )} */}
              <Link className='searchresults-next' component="button" onClick={handleNextClick} disabled={currentPage === totalPages}>{`Next`}</Link>
            </Box>
          ) : (   
            <>              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                <Link
                  className={`searchresults-page ${currentPage === page ? 'selected-page' : ''}`}
                  key={page}
                  component="button"
                  onClick={() => handlePageClick(page)}
                  disabled={currentPage === page}
                >
                  {page}
                </Link>
              ))}
            </>
          )}
        </Box>
        </Box>
        <Card className='searchresults-card'>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">

            <TableHead>
              <TableRow>
                {['Policy/ Quote#', 'Last modified', 'Agent#', 'Status', 'Name', 'City', 'State', 'Zip', 'Product'].map((header) => {
                  const columnKey = keyMap[header] || header.replace(/[#/\s]/g, '').toLowerCase();
                  const isSorted = sortColumn === columnKey;

                  return (
                    <StyledTableCell
                      key={header}
                      className={'quote-policy-column'}
                      onClick={() => handleSortClick(columnKey)}
                    >
                      
                    <span className="quote-header-container">
                      <span className="quote-header-main">
                        {header}
                        {header === 'Policy/ Quote#' && (
                          <span className="policy-quote-subheader">
                            * Indicating Policy#
                          </span>
                        )}
                      </span>
                      {isSorted && (
                        sortDirection === 'asc'
                          ? <ExpandLessIcon className="sort-icon" />
                          : <ExpandMoreIcon className="sort-icon" />
                      )}
                    </span>
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>

              <TableBody>
                {currentRows.map((row) => (
                  <StyledTableRow key={row.quotePolicyNum}>
                    <StyledTableCell>
                      <Link className="quote-policy-column"
                        onClick={async (e) => {
                          e.preventDefault();
                          const newURL = await handleNavigate(row.quotePolicyNum.replace(/#/g, ''));
                          if (newURL) {
                            window.open(newURL, '_self');
                          } 
                        }} 
                      > {row.quotePolicyNum}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell>{row.lastModified}</StyledTableCell>
                    <StyledTableCell>{row.agentNum}</StyledTableCell>
                    <StyledTableCell>{row.status}</StyledTableCell>
                    <StyledTableCell>{row.name}</StyledTableCell>
                    <StyledTableCell>{row.city}</StyledTableCell>
                    <StyledTableCell>{row.state}</StyledTableCell>
                    <StyledTableCell>{row.zip}</StyledTableCell>
                    <StyledTableCell>{row.product}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Box className='searchresults-table-info-bottom'>
          <Box className='searchresults-results-box'>
          <Box className='searchresults-sub-message'>Viewing {indexOfFirstRow + 1}–{indexOfLastRow > rows.length ? rows.length : indexOfLastRow} of {rows.length} results</Box>
          {searchResults.length > 10 ? (
            <Box className='searchresults-pagination'>
              <Link className='searchresults-prev' component="button" onClick={handlePrevClick} disabled={currentPage === 1}>{`Prev`}</Link>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page =>
                  <Link
                    className={`searchresults-page ${currentPage === page ? 'selected-page' : ''}`}
                    key={page}
                    component="button"
                    onClick={() => handlePageClick(page)}
                    disabled={currentPage === page}
                  >
                    {page}
                  </Link>
                )}
                {/* {totalPages > 3 && (
                  <>
                    <span>...</span>
                    <Link
                      className={`searchresults-page ${currentPage === totalPages ? 'selected-page' : ''}`}
                      component="button" onClick={() => handlePageClick(totalPages)}>{totalPages}
                    </Link>
                  </>
                )} */}
              <Link className='searchresults-next' component="button" onClick={handleNextClick} disabled={currentPage === totalPages}>{`Next`}</Link>
            </Box>
          ) : (   
            <>              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                <Link
                  className={`searchresults-page ${currentPage === page ? 'selected-page' : ''}`}
                  key={page}
                  component="button"
                  onClick={() => handlePageClick(page)}
                  disabled={currentPage === page}
                >
                  {page}
                </Link>
              ))}
            </>
          )}
          </Box>
        </Box>
      </>
      )}
    </Box>
  )
}

export default SearchResultsComponent;